import { Controller } from "@hotwired/stimulus"

/**
 * Bag Checkout State Controller
 * 
 * Manages state persistence and restoration for the checkout flow.
 * Handles:
 * - Restoring checkout state on page load
 * - Marking sections as completed
 * - Managing section completion state
 */
export default class extends Controller {
    static targets = ["section"]

    connect() {
        console.log('Bag checkout state controller connected');
        
        // Restore state from form fields on page load
        // Use setTimeout to ensure all controllers are connected first
        setTimeout(() => this.restoreCheckoutState(), 0);
        
        // Listen for section completion events from main controller
        this.element.addEventListener('bag--checkout:sectionCompleted', this.handleSectionCompleted.bind(this));
    }
    
    disconnect() {
        this.element.removeEventListener('bag--checkout:sectionCompleted', this.handleSectionCompleted.bind(this));
    }

    /**
     * Restore checkout state based on filled fields
     */
    restoreCheckoutState() {
        const emailField = this.element.querySelector('[name="order[email]"]');
        const email = emailField?.value || '';
        const shippingName = this.element.querySelector('[name="order[ship_firstname]"]')?.value;
        
        // Check if contact section was completed
        if (email && email.trim() !== '') {
            this.completeSection(1, email);
            
            // Check if shipping section was completed
            if (shippingName && shippingName.trim() !== '') {
                this.completeSection(2, '');
                
                // Dispatch event to open payment section
                this.dispatch('restoreComplete', { 
                    detail: { openSection: 3 } 
                });
            } else {
                // Open shipping section since contact is done
                this.dispatch('restoreComplete', { 
                    detail: { openSection: 2 } 
                });
            }
        } else {
            // New checkout - open Section 1 (Contact Information)
            console.log('New checkout - opening Section 1');
            this.dispatch('restoreComplete', { 
                detail: { openSection: 1 } 
            });
        }
    }

    /**
     * Handle section completion events
     */
    handleSectionCompleted(event) {
        const { section, email } = event.detail;
        this.completeSection(section, email || '');
    }

    /**
     * Mark a section as completed
     */
    completeSection(sectionNum, summary) {
        const section = this.sectionTargets[sectionNum - 1];
        if (!section) return;
        
        const sectionBody = section.querySelector('[data-bag--checkout-target="sectionBody"]');
        const sectionHeader = section.querySelector('[data-bag--checkout-target="sectionHeader"]');
        const editLink = section.querySelector(`[data-bag--checkout-target="editLink${sectionNum}"]`);
        
        // Add completed class and remove active class
        section.classList.add('completed');
        section.classList.remove('active');
        
        // Hide body
        if (sectionBody) {
            sectionBody.style.display = 'none';
        }
        
        // Update ARIA attributes
        if (sectionHeader) {
            sectionHeader.setAttribute('aria-expanded', 'false');
        }
        
        // Show edit link
        if (editLink) {
            editLink.style.display = 'inline';
            editLink.innerHTML = '<i class="bi bi-pencil me-1" aria-hidden="true"></i>Edit';
        }
        
        // Dispatch event for summary controller to show summary
        this.dispatch('showSummary', { 
            detail: { section: sectionNum, summary } 
        });
    }
}
